import { Banner, Button, H3, Modal, Detail, Row, Caption, Icon, Grid } from "@maggioli-design-system/react";
import * as Constants from "../lib/Constants";
import { Accordion, AccordionItem } from "./Accordion";
import { IFeedback } from "../Interfaces/IFeedback";
import ReactMarkdown from "react-markdown"
import AIGenerated from "./AIGenerated";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faThumbsDown, faThumbsUp } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";
import { askQuestionJob, checkAipenRole, checkUser, getQAInBook } from "../services/AIpen";
import { IBook } from "../Interfaces/IBook";
import { IQuestion } from "../Interfaces/AipenJobs";
import { checkExistingFeedback, checkFeedback, givenFeedbackIsDown, givenFeedbackIsUp } from "../lib/Functions";
import { TailSpin } from "react-loader-spinner";
import { ICON_BUTTON } from "../lib/A11y";

interface IQAs {
    book: IBook,
    placeholder?: string,
    setShowModal: Function,
    givenFeedback: IFeedback[],
    updateFeedback: Function,
    feedbackSetter: any
}

const oneMonthInSeconds = 60 * 60 * 24 * 31;

const QAs = ({ book, placeholder = "", setShowModal, givenFeedback, updateFeedback, feedbackSetter }: IQAs): JSX.Element => {
    
    const userIsLogged: boolean = Constants.getCookie("refresh_token") !== null;
    const userOnAipenCookie: null | string = Constants.getCookie("user_on_aipen");
    const aipenID: number = book.aipenDocumentiId;

    const [copy, setCopy] = useState<boolean>(false);
    const [showQAinfo, setShowQAinfo] = useState<boolean>(false);
    const [generating, setGenerating] = useState<boolean>(false);
    const [longWait, setLongWait] = useState<boolean>(false);
    const [newQuestion, setNewQuestion] = useState<string>("");
    const [openedAnswer, setOpenedAnswer] = useState<number>(0);
    const [questions, setQuestions] = useState<IQuestion[]>([]);

    useEffect(() => {
        if(book.aipenInitStatus !== "COMPLETED" || !userIsLogged) {
            return;
        }
        loadQA();
    }, [])

    const loadQA = async () => {
        let userOnAipen = userOnAipenCookie !== null ? userOnAipenCookie === "true" : false;
        if(userOnAipenCookie === null) {
            const res1 = await checkAipenRole();
            Constants.setCookie("user_on_aipen", res1.data.result, oneMonthInSeconds);
            userOnAipen = res1.data.result;
        }
        if(!userOnAipen) {
            return;
        }
        const res2 = await getQAInBook(aipenID)
        if (res2.status !== 200 || !Array.isArray(res2.data)) {
            return;
        }
        setQuestions(res2.data);
    }

    const onClickCopyAnswerText = (e, text: string) => {
        e.stopPropagation();
        navigator.clipboard.writeText(text);
        setCopy(true);
        setTimeout(() => setCopy(false), 2000);
    }

    const onClickGenerateAnswer = async () => {

        if(generating) {
            return;
        }

        if(!userIsLogged || !book.hasAccessToExtraContent) {
            setShowModal(true);
            return;
        }

        if(newQuestion.length < 10) {
            alert("La domanda deve essere lunga almeno 10 caratteri.");
            return;
        }

        const userCheckedCookie = Constants.getCookie("user_on_aipen");
        if((userCheckedCookie && userCheckedCookie === "false") || !userCheckedCookie) {
            const checkUserRes = await checkUser();
            if (checkUserRes.status) {
                Constants.setCookie("user_on_aipen", "true", oneMonthInSeconds);
            }
        }

        const payload = {
            task: "q&a",
            args: {
                language: "it",
                question: newQuestion,
            },
        };
        setGenerating(true);
        setNewQuestion("");
        askQuestionJob(payload, aipenID).then(res => {
            if (!res.data.status) {
                setGenerating(false);
                alert("Errore nella generazione della risposta");
                return;
            }

            const initialQuestionsCount = questions.length;

            const checkForAnswer = async (): Promise<boolean> => {
                const res2 = await getQAInBook(aipenID);
                if (res2.status !== 200 || !Array.isArray(res2.data)) {
                    return false;
                }
                if (res2.data.length > initialQuestionsCount) {
                    setQuestions(res2.data);
                    setOpenedAnswer(res2.data[0].id);
                    return true;
                }
                return false;
            };

            setTimeout(async () => {
                if (await checkForAnswer()) {
                    setGenerating(false);
                    return;
                }

                const pollInterval = setInterval(async () => {
                    setLongWait(true);
                    if (await checkForAnswer()) {
                        clearInterval(pollInterval);
                        setGenerating(false);
                        setLongWait(false);
                    }
                }, 3000);
            }, 5000);

        })
    }
    
    return <Grid rows="fit-vertically gap-10">
        { aipenID && 
            <div className="rounded-xl grid gap-5 -mb-5">
                <Grid className={`rounded-2xl transition-all gap-1 p-0 bg-transparent`}>

                    <H3 className="items-center inline-flex gap-2">Inserisci la tua domanda<AIGenerated/></H3>
                    
                    <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); onClickGenerateAnswer(); }}>
                        { Constants.searchbar({
                            text: newQuestion,
                            setText: setNewQuestion,
                            url: questions.length.toString(),
                            placeholder: placeholder,
                            id: "domanda-ia",
                            label: "Inserisci la tua domanda per l'assistente IA",
                            isSearch: false,
                        }) }
                        <Button type="submit" aria-label="Invia la domanda" icon="data-send" variant="primary-outline" className="w-8" disabled={generating}/>
                    </form>
                    
                </Grid>
                    
                <div>
                    { generating ?
                    <div className="flex items-center">
                        <span className="italic text-sm">{ !longWait ? "Generazione della risposta in corso..." : "Attendi per favore..."}</span>
                        <TailSpin color="#0041B9" height={40}/>
                    </div> :
                    <Button variant="secondary-outline" onClick={() => setShowQAinfo(true)}>Come scrivere una domanda efficace</Button> }
                </div>
                {/* Lo stato di elaborazione va comunicato anche a chi non vede il pulsante */}
                <div aria-live="polite" className="sr-only" role="status">
                    { generating ? "Sto cercando la risposta, attendere." : "" }
                </div>
            </div> }

            {/* { infoText && 
            <Banner status="warning" className="w-full rounded-2xl text-secondary text-secondary--detail">
                La generazione della risposta sta richiedendo più tempo del previsto. 
                Ti invitiamo a tornare su questa pagina tra qualche secondo.
            </Banner> } */}

            <Modal visible={showQAinfo} onCancel={() => setShowQAinfo(false)} position="center" footer={false}>

                <Detail>
                    Questo servizio fornisce risposte dirette basate su informazioni specifiche tratte dai contenuti dell'opera editoriale.
                    A differenza di un chatbot conversazionale, che gestisce dialoghi interattivi e dinamici, qui puoi porre domande mirate e ricevere risposte precise e contestualizzate.
                </Detail>
                <Detail className="mt-3">
                    <strong>Suggerimento:</strong> per ottenere risultati più accurati, formula la tua domanda in modo chiaro e specifico. Ad esempio, invece di chiedere "Cognome donna estero", prova con "Come si attribuisce il cognome di una donna italiana sposata con un cittadino straniero?".
                </Detail>
                
            </Modal>

            <Accordion>
                { questions.map((question, i) => {

                    const selectedFeedback: IFeedback | false = checkExistingFeedback(question.id, givenFeedback);

                    const onClickFeedback = (e: any, value: boolean) => {
                        e.stopPropagation();
                        feedbackSetter.setJobIDFeedback(question.id); 
                        feedbackSetter.setFeedback(true);
                        feedbackSetter.setAlreadyDone(selectedFeedback);
                        feedbackSetter.setRank(selectedFeedback ? selectedFeedback.vote : value);
                    }

                    return <AccordionItem key={i} title={question.question} opened={openedAnswer === question.id}
                            onToggle={() => checkFeedback(question.id, givenFeedback, updateFeedback)}>
                            <ReactMarkdown className="text-lg">
                                {question.answer}
                            </ReactMarkdown>
                            <Row lastChild="to-right">
                                <button className={'mt-3 cursor-pointer inline-flex items-center gap-2 bg-adjust-tone-19 rounded-full px-4 py-1.5 border-0'}
                                    onClick={e => onClickCopyAnswerText(e, question.answer)} type="button">
                                    <div aria-hidden="true" className="bg-adjust-tone-04 rounded-full inline-flex items-center justify-center w-6 h-6 text-adjust-tone">
                                        <Icon name={copy ? "status-success" : "action-copy"} size="small"></Icon>
                                    </div>
                                    <Caption>
                                        { copy ? "Testo copiato!" : "Copia testo" }
                                    </Caption>
                                </button>
                                <div className="mt-3 flex gap-2">
                                    <AIGenerated/>
                                    <button aria-label="Valuta positivamente la risposta" aria-pressed={selectedFeedback !== false && selectedFeedback.vote}
                                        className={ICON_BUTTON} onClick={e => onClickFeedback(e, true)} type="button">
                                        <FontAwesomeIcon aria-hidden="true" icon={faThumbsUp} className={`h-6 ${givenFeedbackIsUp(selectedFeedback)}`}/>
                                    </button>
                                    <button aria-label="Valuta negativamente la risposta" aria-pressed={selectedFeedback !== false && !selectedFeedback.vote}
                                        className={`${ICON_BUTTON} mt-1`} onClick={e => onClickFeedback(e, false)} type="button">
                                        <FontAwesomeIcon aria-hidden="true" icon={faThumbsDown} className={`h-6 ${givenFeedbackIsDown(selectedFeedback)}`}/>
                                    </button>
                                </div>
                            </Row>
                        </AccordionItem>
                })}
            </Accordion>
    </Grid>
};

export default QAs;
